// src/App.js
import React, { useState, useEffect } from 'react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import axios from 'axios';

// Import Pages
import HomePage from './pages/HomePage';
import ShopPage from './pages/ShopPage';
import CheckoutPage from './pages/CheckoutPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import DashboardPage from './pages/DashboardPage';
import ProfilePage from './pages/ProfilePage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import LoadingScreen from './components/common/LoadingScreen';

// Configuration
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const API_URL = process.env.REACT_APP_API_URL;

function App() {
  const [currentPage, setCurrentPage] = useState('home');
  const [cart, setCart] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);

  // Detect mobile screen
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Initialize app
  useEffect(() => {
    const initializeApp = async () => {
      try {
        const savedUser = localStorage.getItem('sirHenryUser');
        const savedCart = localStorage.getItem('sirHenryCart');
        
        if (savedUser) {
          const userData = JSON.parse(savedUser);
          setUser(userData);
          
          if (userData.token) {
            try {
              const response = await axios.get(`${API_URL}/index.php`, {
                headers: {
                  'Authorization': `Bearer ${userData.token}`,
                  'usertypeauth': 'User'
                }
              });
              
              if (response.data.success) {
                setUser(prev => ({ ...prev, ...response.data.user }));
              } else {
                handleLogout();
              }
            } catch (error) {
              console.error('Token verification failed:', error);
            }
          }
        }

        if (savedCart) {
          setCart(JSON.parse(savedCart));
        }

        await fetchProducts();
        await fetchFeaturedProducts();

      } catch (error) {
        console.error('Initialization error:', error);
        initializeDefaultProducts();
      } finally {
        setIsLoading(false);
      }
    };

    initializeApp();
  }, []);

  // Fetch functions
  const fetchProducts = async () => {
    try {
      const currentUser = user || JSON.parse(localStorage.getItem('sirHenryUser') || 'null');
      const token = currentUser?.token;
      const config = {
        headers: token ? {
          'Authorization': `Bearer ${token}`,
          'usertypeauth': 'User'
        } : {}
      };

      const response = await axios.post(`${API_URL}/index.php`, {
        apiType: "getProducts",
        userRole: currentUser?.role || 'customer',
        userID: currentUser?.id || 0
      }, config);
      
      if (response.data.success) {
        setProducts(response.data.products);
        localStorage.setItem('sirHenryProducts', JSON.stringify(response.data.products));
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      const savedProducts = localStorage.getItem('sirHenryProducts');
      if (savedProducts) {
        setProducts(JSON.parse(savedProducts));
      }
    }
  };

  const fetchFeaturedProducts = async () => {
    try {
      const response = await axios.post(`${API_URL}/index.php`, {
        apiType: "getFeaturedProducts"
      });
      
      if (response.data.success) {
        setFeaturedProducts(response.data.featuredProducts);
        localStorage.setItem('sirHenryFeaturedProducts', JSON.stringify(response.data.featuredProducts));
      }
    } catch (error) {
      console.error('Error fetching featured products:', error);
      const savedFeatured = localStorage.getItem('sirHenryFeaturedProducts');
      if (savedFeatured) {
        setFeaturedProducts(JSON.parse(savedFeatured));
      }
    }
  };

  const initializeDefaultProducts = () => {
    const savedProducts = localStorage.getItem('sirHenryProducts');
    const savedFeatured = localStorage.getItem('sirHenryFeaturedProducts');

    if (!savedProducts) {
      const defaultProducts = [
        {
          id: 1,
          name: "Premium Pork Legs",
          image: "https://images.unsplash.com/photo-1550949987-33f716ccc232?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
          description: "Perfect for roasting and special occasions. Our pork legs are carefully selected for optimal flavor and tenderness.",
          price: 15.99,
          weight: "1kg",
          category: "Premium Cuts",
          isWholesale: false,
          isActive: true
        },
        {
          id: 2,
          name: "Pork Shoulders",
          image: "https://images.unsplash.com/photo-1604503468506-a8da13d82791?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
          description: "Ideal for slow cooking and pulled pork. These shoulders are perfect for BBQ and family meals.",
          price: 12.99,
          weight: "1kg",
          category: "Premium Cuts",
          isWholesale: false,
          isActive: true
        }
      ];
      setProducts(defaultProducts);
      localStorage.setItem('sirHenryProducts', JSON.stringify(defaultProducts));
    } else {
      setProducts(JSON.parse(savedProducts));
    }

    if (!savedFeatured) {
      const defaultFeatured = [
        {
          id: 1,
          name: "Pork Legs",
          image: "https://images.unsplash.com/photo-1550949987-33f716ccc232?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
          description: "Perfect for roasting and special occasions"
        },
        {
          id: 2,
          name: "Pork Shoulders",
          image: "https://images.unsplash.com/photo-1604503468506-a8da13d82791?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
          description: "Ideal for slow cooking and pulled pork"
        }
      ];
      setFeaturedProducts(defaultFeatured);
      localStorage.setItem('sirHenryFeaturedProducts', JSON.stringify(defaultFeatured));
    } else {
      setFeaturedProducts(JSON.parse(savedFeatured));
    }
  };

  // Cart persistence
  useEffect(() => {
    localStorage.setItem('sirHenryCart', JSON.stringify(cart));
  }, [cart]);

  // User persistence
  useEffect(() => {
    if (user) {
      localStorage.setItem('sirHenryUser', JSON.stringify(user));
    } else {
      localStorage.removeItem('sirHenryUser');
    }
  }, [user]);

  // Cart functions
  const addToCart = (product, quantity, purchaseType, customAmount = 0) => {
    setCart(prevCart => {
      const existingItemIndex = prevCart.findIndex(item => 
        item.id === product.id && item.purchaseType === purchaseType
      );
      
      if (existingItemIndex > -1) {
        return prevCart.map((item, index) =>
          index === existingItemIndex
            ? { 
                ...item, 
                quantity: purchaseType === 'amount' ? item.quantity : item.quantity + quantity,
                customAmount: purchaseType === 'amount' ? customAmount : item.customAmount
              }
            : item
        );
      } else {
        return [...prevCart, { 
          ...product, 
          quantity: purchaseType === 'amount' ? 1 : quantity,
          purchaseType,
          customAmount: purchaseType === 'amount' ? customAmount : 0,
          calculatedWeight: purchaseType === 'amount' ? (customAmount / product.price).toFixed(2) : null
        }];
      }
    });
  };

  const removeFromCart = (productId) => {
    setCart(prevCart => prevCart.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity < 0.1) return;
    setCart(prevCart =>
      prevCart.map(item =>
        item.id === productId
          ? { ...item, quantity: newQuantity }
          : item
      )
    );
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => {
      if (item.purchaseType === 'amount') {
        return total + parseFloat(item.customAmount);
      } else {
        return total + (item.price * item.quantity);
      }
    }, 0);
  };

  const getCartItemCount = () => {
    return cart.reduce((total, item) => {
      if (item.purchaseType === 'amount') {
        return total + 1;
      } else {
        return total + item.quantity;
      }
    }, 0);
  };

  // Auth functions
  const handleLogin = (userData) => {
    setUser(userData);
    setCurrentPage('dashboard');
  };

  const handleLogout = () => {
    setUser(null);
    setCart([]);
    setCurrentPage('home');
    localStorage.removeItem('sirHenryUser');
    localStorage.removeItem('sirHenryCart');
  };

  // Update functions
  const updateProducts = (newProducts) => {
    setProducts(newProducts);
    localStorage.setItem('sirHenryProducts', JSON.stringify(newProducts));
  };

  const updateFeaturedProducts = (newFeaturedProducts) => {
    setFeaturedProducts(newFeaturedProducts);
    localStorage.setItem('sirHenryFeaturedProducts', JSON.stringify(newFeaturedProducts));
  };

  // Render page
  const renderPage = () => {
    if (isLoading) {
      return <LoadingScreen />;
    }

    switch (currentPage) {
      case 'home':
        return (
          <HomePage 
            isMobile={isMobile}
            setCurrentPage={setCurrentPage}
            featuredProducts={featuredProducts}
            cartItemCount={getCartItemCount()}
            user={user}
            onLogout={handleLogout}
          />
        );
      case 'shop':
        return (
          <ShopPage 
            isMobile={isMobile}
            setCurrentPage={setCurrentPage}
            products={products}
            addToCart={addToCart}
            cartItemCount={getCartItemCount()}
            user={user}
            onLogout={handleLogout}
          />
        );
      case 'checkout':
        return (
          <CheckoutPage 
            isMobile={isMobile}
            setCurrentPage={setCurrentPage}
            cart={cart}
            removeFromCart={removeFromCart}
            updateQuantity={updateQuantity}
            getCartTotal={getCartTotal}
            clearCart={() => setCart([])}
            user={user}
            cartItemCount={getCartItemCount()}
            onLogout={handleLogout}
          />
        );
      case 'login':
        return (
          <LoginPage 
            isMobile={isMobile}
            setCurrentPage={setCurrentPage}
            onLogin={handleLogin}
          />
        );
      case 'signup':
        return (
          <SignupPage 
            isMobile={isMobile}
            setCurrentPage={setCurrentPage}
            onLogin={handleLogin}
          />
        );
      case 'dashboard':
        return (
          <DashboardPage 
            isMobile={isMobile}
            setCurrentPage={setCurrentPage}
            user={user}
            onLogout={handleLogout}
            products={products}
            featuredProducts={featuredProducts}
            updateProducts={updateProducts}
            updateFeaturedProducts={updateFeaturedProducts}
            fetchProducts={fetchProducts}
            fetchFeaturedProducts={fetchFeaturedProducts}
          />
        );
      case 'profile':
        return (
          <ProfilePage 
            isMobile={isMobile}
            setCurrentPage={setCurrentPage}
            user={user}
            onLogout={handleLogout}
            onUserUpdate={setUser}
          />
        );
      case 'change-password':
        return (
          <ChangePasswordPage 
            isMobile={isMobile}
            setCurrentPage={setCurrentPage}
            user={user}
            onLogout={handleLogout}
          />
        );
      default:
        return (
          <HomePage 
            isMobile={isMobile}
            setCurrentPage={setCurrentPage}
            featuredProducts={featuredProducts}
            cartItemCount={getCartItemCount()}
            user={user}
            onLogout={handleLogout}
          />
        );
    }
  };

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <div className="font-sans bg-gradient-to-br from-gray-50 via-white to-gray-100">
        {renderPage()}
      </div>
    </GoogleOAuthProvider>
  );
}

export default App;